import React, { useState, useEffect } from 'react';
import cl from './Style.module.css';
import AdminService from '../../API/AdminService';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../components/UI/Spinner/Spinner';
import logger from '../../utils/logger';
import PageIntro from '../../components/PageIntro/PageIntro';
import {
    IconCalendar, IconClock, IconUser, IconPlus, IconRefresh, IconBook,
} from '../../components/Icons';
const Jornal = () => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Дни недели с соответствующими числовыми значениями
    const daysOfWeek = [
        { id: 1, name: 'Понедельник', shortName: 'Пн' },
        { id: 2, name: 'Вторник', shortName: 'Вт' },
        { id: 3, name: 'Среда', shortName: 'Ср' },
        { id: 4, name: 'Четверг', shortName: 'Чт' },
        { id: 5, name: 'Пятница', shortName: 'Пт' },
        { id: 6, name: 'Суббота', shortName: 'Сб' },
        { id: 0, name: 'Воскресенье', shortName: 'Вс' }
    ];

    // Загрузка курсов
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const coursesResponse = await AdminService.getAllCourses();

            // Фильтруем только активные курсы
            const activeCourses = (coursesResponse.data || []).filter(
                course => course.status === 'Активный' || !course.status
            );

            setCourses(activeCourses);
        } catch (error) {
            logger.error('Ошибка загрузки данных:', error);
            setError('Ошибка загрузки расписания');
        } finally {
            setLoading(false);
        }
    };

    // Получить курсы для конкретного дня
    const getCoursesForDay = (dayId) => {
        const dayCourses = [];

        // Добавляем курсы для этого дня
        courses.forEach(course => {
            if (course.day === dayId) {
                dayCourses.push({
                    ...course,
                    type: 'course',
                    displayName: course.name,
                    startTime: course.timeStart,
                    endTime: course.timeEnd,
                    ageRange: course.ageRange || '8-12'
                });
            }
        });

        // Сортируем по времени начала
        return dayCourses.sort((a, b) => {
            if (!a.startTime && !b.startTime) return 0;
            if (!a.startTime) return 1;
            if (!b.startTime) return -1;
            return a.startTime.localeCompare(b.startTime);
        });
    };

    // Форматирование времени для отображения
    const formatTime = (timeStart, timeEnd) => {
        if (!timeStart && !timeEnd) return '';
        if (timeStart && timeEnd) return `${timeStart} - ${timeEnd}`;
        if (timeStart) return `${timeStart}`;
        return '';
    };

    // Форматирование дат курса
    const formatDateRange = (dateStart, dateEnd) => {
        if (!dateStart && !dateEnd) return '';
        if (dateStart && dateEnd && dateStart === dateEnd) return dateStart;
        if (dateStart && dateEnd) return `${dateStart} - ${dateEnd}`;
        if (dateStart) return `с ${dateStart}`;
        if (dateEnd) return `до ${dateEnd}`;
        return '';
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <Spinner size="large" text="Загрузка расписания" fullPage />
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.error}>{error}</div>
                </div>
            </div>
        );
    }

    // Обработка клика на курс
    const handleCourseClick = (course) => {
        navigate(`/main/journal/course/${course.id}`);
    };

    const handleCreateCourse = () => {
        navigate(`/main/settings`);
    }



    // Шапка-карточка раздела + кнопки действий.
    // Используется и при пустом списке, и при наполненном расписании.
    const renderPageHeader = () => (
        <div className={cl.pageHeader}>
            <div className={cl.pageHeaderLeft}>
                <div className={cl.pageHeaderIcon}>
                    <IconCalendar width={22} height={22}/>
                </div>
                <div className={cl.pageHeaderText}>
                    <div className={cl.pageHeaderTopRow}>
                        <h1 className={cl.pageHeaderTitle}>Расписание занятий</h1>
                        {courses.length > 0 && (
                            <span className={cl.pageHeaderPill}>
                                <span className={cl.pageHeaderPillStrong}>{courses.length}</span>
                                <span className={cl.pageHeaderPillMuted}>{courses.length === 1 ? 'курс' : 'курсов'}</span>
                            </span>
                        )}
                    </div>
                    <div className={cl.breadcrumbs}>
                        АдминПанель · Расписание занятий
                    </div>
                </div>
            </div>
            <div className={cl.pageHeaderActions}>
                <button
                    onClick={fetchData}
                    className={cl.btnGhost}
                    title="Обновить"
                >
                    <IconRefresh width={15} height={15}/>
                </button>
                <button
                    onClick={() => navigate('/main/edit-courses')}
                    className={cl.addButton}
                >
                    <IconPlus width={16} height={16}/>
                    <span>Создать курс</span>
                </button>
            </div>
        </div>
    );

    // Пустое состояние — нет курсов
    if (courses.length === 0) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    {renderPageHeader()}
                    <PageIntro
                        storageKey="jornal"
                        title="О странице «Расписание занятий»"
                        text="Все активные курсы вашего центра, разложенные по дням недели. Курс — это группа учеников, которая регулярно занимается в одно и то же время с одним преподавателем."
                        bullets={[
                            'Курсы создаются в разделе «Настройки» — там же задаются день, время и преподаватель',
                            'Клик на карточку курса — журнал посещаемости и оплаты группы',
                            'Дни без занятий не показываются',
                        ]}
                    />
                    <div className={cl.emptyState}>
                        <div className={cl.emptyIconWrap}>
                            <IconCalendar width={36} height={36}/>
                        </div>
                        <h2 className={cl.emptyTitle}>Нет активных курсов</h2>
                        <p className={cl.emptyDescription}>
                            В расписании пока ничего нет. Создайте курсы в настройках —
                            и они появятся здесь, разложенные по дням недели.
                        </p>
                        <div className={cl.emptyActions}>
                            <button
                                className={cl.addButton}
                                onClick={() => navigate('/main/edit-courses')}
                            >
                                <IconPlus width={16} height={16}/>
                                <span>Создать курс</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {renderPageHeader()}

                <PageIntro
                    storageKey="jornal"
                    title="О странице «Расписание занятий»"
                    text="Все активные курсы вашего центра, разложенные по дням недели. Курс — это группа учеников, которая регулярно занимается в одно и то же время с одним преподавателем. Здесь видно общую картину: где-то загруженность, где-то «окно» под новый поток."
                    bullets={[
                        'Курсы создаются в разделе «Настройки» — там же задаются день, время и преподаватель',
                        'Клик на карточку курса — журнал посещаемости и оплаты группы',
                        'Дни без занятий не показываются — расписание не растягивается зря',
                        'Кнопка «Создать курс» сверху — быстрый переход в редактор',
                    ]}
                />

                {/* Сетка дней недели */}
                <div className={cl.scheduleGrid}>
                    {daysOfWeek.map(day => {
                        const dayEvents = getCoursesForDay(day.id);
                        if (dayEvents.length === 0) return null;

                        return (
                            <div key={day.id} className={cl.dayColumn}>
                                <div className={cl.dayHeader}>
                                    <span className={cl.dayName}>{day.name}</span>
                                    <span className={cl.dayCount}>
                                        {dayEvents.length}
                                    </span>
                                </div>
                                <div className={cl.dayContent}>
                                    {dayEvents.map((course) => (
                                        <div
                                            key={`${course.type}-${course.id}`}
                                            className={cl.courseCard}
                                            onClick={() => handleCourseClick(course)}
                                        >
                                            <div className={cl.courseCardHead}>
                                                <h4 className={cl.courseCardTitle}>
                                                    {course.displayName}
                                                </h4>
                                            </div>
                                            <div className={cl.courseCardMeta}>
                                                {(course.startTime || course.endTime) && (
                                                    <span className={cl.courseCardRow}>
                                                        <IconClock width={13} height={13}/>
                                                        {formatTime(course.startTime, course.endTime)}
                                                    </span>
                                                )}
                                                {course.prepod && (
                                                    <span className={cl.courseCardRow}>
                                                        <IconUser width={13} height={13}/>
                                                        {course.prepod}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Jornal;